import {
  DOCUMENT_RISK_SCORE_FILTER_ENABLED,
  DOCUMENT_RISK_SCORE_FILTER_OPTIONS,
  isAddedManuallyVendor,
  isRequestedInPlatformVendor,
} from "./myVendorsConstants";
import { resolveRequestStatusKey } from "./myVendorsRequestUtils";
import { extractVendorListMembers } from "./myVendorListsUtils";
import { SYSTEM_LIST_IDS } from "./myVendorListsConstants";
import { collectVendorRiskRatings } from "./myVendorRiskUtils";
import { getVendorNameAscendingSortOrder } from "./myVendorSortUtils";
import { ensureVendorGxPProfile } from "./myVendorGxpUtils";

export {
  compareVendorNamesAscending,
  getVendorNameAscendingSortOrder,
  sortVendorsByNameAscending,
} from "./myVendorSortUtils";

/** Hierarchical Platform / Manual options — prototype within-list parity. */
export const VENDOR_LIST_PLATFORM_MANUAL_OPTIONS = [
  {
    value: "requested",
    label: "Requested in Platform",
    isParent: true,
    children: ["new-request", "library-request"],
  },
  { value: "new-request", label: "New Request", indent: true, parent: "requested" },
  { value: "library-request", label: "Library Request", indent: true, parent: "requested" },
  { value: "manual", label: "Added Manually" },
];

const GXP_DESCRIPTORS = {
  GAMP: "GAMP - Automated Manufacturing",
  GCDMP: "GCDMP - Clinical Data Management",
  GCP: "GCP - Clinical",
  GDocP: "GDocP - Documentation",
  GDP: "GDP - Distribution",
  GEP: "GEP - Engineering",
  GLP: "GLP - Laboratory",
  GMLP: "GMLP - Machine Learning",
  GMP: "GMP - Manufacturing",
  GPP: "GPP - Pharmacy",
  GSP: "GSP - Storage",
  GTP: "GTP - Tissue",
  "GTraP/GTrP": "GTraP/GTrP - Training",
  GVP: "GVP - Pharmacovigilance",
};

const REQUEST_STATUS_FILTER_OPTIONS = [
  { value: "In Progress", label: "In Progress" },
  { value: "Complete", label: "Complete" },
];

const REQUEST_STATUS_COMPLETE_VALUES = new Set(["complete", "completed"]);
const REQUEST_STATUS_IN_PROGRESS_VALUES = new Set(["in progress", "pending"]);

/** Prototype within-list filter keys (`tags`, not `customTags`). */
export const EMPTY_VENDOR_LIST_VIEW_SELECTED_IDS = {
  type: [],
  platformManual: [],
  gxp: [],
  services: [],
  addedBy: [],
  tags: [],
  requestStatus: [],
  riskScores: [],
};

export function getDefaultManualSortOrder(vendors = []) {
  return getVendorNameAscendingSortOrder(vendors);
}

/** Persisted manual order for user lists — member sort_order from the API. */
export function getListPersistedSortOrder(vendors = []) {
  return vendors
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((vendor) => String(vendor.id))
    .filter(Boolean);
}

/** @deprecated alias */
export function getListDefaultSortOrder(vendors = []) {
  return getListPersistedSortOrder(vendors);
}

function parseMemberDateAddedTimestamp(vendor) {
  const raw = vendor?.dateAddedRaw ?? vendor?.dateAdded;
  if (!raw) return null;
  const parsed = Date.parse(String(raw));
  return Number.isNaN(parsed) ? null : parsed;
}

/** Original display order for user lists — date added, frozen at load for the manual-sort toggle. */
export function getListDateAddedSortOrder(vendors = []) {
  return vendors
    .slice()
    .sort((a, b) => {
      const av = parseMemberDateAddedTimestamp(a);
      const bv = parseMemberDateAddedTimestamp(b);
      if (av !== null && bv !== null && av !== bv) return av - bv;
      if (av !== null && bv === null) return -1;
      if (av === null && bv !== null) return 1;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      });
    })
    .map((vendor) => String(vendor.id))
    .filter(Boolean);
}

/** Keep stored order; append vendors that joined the list later. */
export function mergeManualSortWithNewMembers(existingOrder = [], vendors = []) {
  const vendorIds = new Set(vendors.map((vendor) => String(vendor.id)));
  const kept = existingOrder.map(String).filter((id) => vendorIds.has(id));
  const keptSet = new Set(kept);
  const appended = vendors
    .map((vendor) => String(vendor.id))
    .filter((id) => id && !keptSet.has(id));
  return [...kept, ...appended];
}

/** Merge a drag reorder of visible rows back into the full list order. */
export function mergeVisibleReorder(fullOrder = [], visibleReorder = []) {
  const full = fullOrder.map(String);
  const visible = visibleReorder.map(String);
  const visibleSet = new Set(visible);
  const visiblePositions = [];

  full.forEach((id, index) => {
    if (visibleSet.has(id)) visiblePositions.push(index);
  });

  if (visiblePositions.length !== visible.length) {
    return full;
  }

  const result = [...full];
  visible.forEach((id, index) => {
    result[visiblePositions[index]] = id;
  });
  return result;
}

export function applyManualSortOrder(vendors = [], manualSortOrder = []) {
  if (!manualSortOrder.length) return vendors;
  const orderMap = new Map(manualSortOrder.map((id, index) => [String(id), index]));
  return [...vendors].sort((a, b) => {
    const aIndex = orderMap.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
}

function formatMemberDateAdded(raw) {
  if (!raw) return null;
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

/** GxPs for filter options + matcher — profile fields first, then request GxPs. */
function collectVendorListViewGxPs(vendor = {}) {
  const fromProfile = [
    ...(vendor.applicableGxPs || []),
    ...(vendor.demonstratedGxPs || []),
    ...(vendor.gxps || []),
  ];
  const fromRequests = [
    ...(vendor.myRequests || []),
    ...(vendor.libraryRequests || []),
  ].flatMap((request) => request.gxps || []);
  return [...fromProfile, ...fromRequests].filter(Boolean);
}

function collectVendorListViewServices(vendor = {}) {
  const profileServices = vendor.serviceCategories || vendor.services || [];
  const requestServices = [
    ...(vendor.myRequests || []),
    ...(vendor.libraryRequests || []),
  ].flatMap((request) => request.services || []);
  return [...profileServices, ...requestServices].filter(Boolean);
}

function collectVendorListViewRequests(vendor = {}) {
  return [...(vendor.myRequests || []), ...(vendor.libraryRequests || [])];
}

function getVendorTagList(vendor = {}) {
  const tags = vendor.tags;
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === "string" && tags.trim()) {
    return tags.split(",").map((tag) => tag.trim()).filter(Boolean);
  }
  return [];
}

/** Join list membership with live My Vendors rows for table rendering. */
export function resolveVendorListTableRows(list, myVendors = [], vendorTagsMap = {}) {
  const byId = new Map(
    myVendors.map((vendor) => [String(vendor.id || "").toLowerCase(), vendor])
  );
  const isBookmarkedList =
    list?.systemKind === "bookmarked" ||
    String(list?.id || "") === SYSTEM_LIST_IDS.BOOKMARKED;

  return extractVendorListMembers(list).map((member, index) => {
    const providerId = member.providerId ? String(member.providerId) : null;
    const live = providerId ? byId.get(providerId.toLowerCase()) : null;

    if (live) {
      return {
        ...live,
        id: providerId || live.id,
        name: isBookmarkedList
          ? member.name || live.name || ""
          : live.name || member.name || "",
        tags: vendorTagsMap[providerId] || live.tags || [],
        addedBy: member.addedByName || null,
        dateAdded: formatMemberDateAdded(member.dateAdded),
        dateAddedRaw: member.dateAdded ?? null,
        sortOrder: member.sortOrder ?? index,
      };
    }

    return ensureVendorGxPProfile({
      id: providerId || member.name,
      name: member.name || "",
      type: null,
      primaryLocation: null,
      logoUrl: member.logoUrl || null,
      serviceCategories: member.serviceCategories || [],
      services: member.serviceCategories || [],
      applicableGxPs: [],
      demonstratedGxPs: [],
      myRequests: [],
      libraryRequests: [],
      rfiCount: 0,
      assessmentCount: 0,
      riskSummary: null,
      lastQualifiedDate: null,
      lastQualifiedRequest: null,
      tags: vendorTagsMap[providerId] || [],
      isManuallyAdded: false,
      isHidden: false,
      hasPlatformActivity: false,
      addedBy: member.addedByName || null,
      dateAdded: formatMemberDateAdded(member.dateAdded),
      dateAddedRaw: member.dateAdded ?? null,
      sortOrder: member.sortOrder ?? index,
    });
  });
}

export function applyVendorListViewSearch(vendors = [], searchQuery = "") {
  const query = String(searchQuery || "").trim().toLowerCase();
  if (!query) return vendors;

  return vendors.filter((vendor) => {
    const nameMatch = vendor.name?.toLowerCase().includes(query);
    const typeMatch = vendor.type?.toLowerCase().includes(query);
    const locationMatch = vendor.primaryLocation?.toLowerCase().includes(query);
    const serviceMatch = (vendor.serviceCategories || vendor.services || []).some((service) =>
      String(service).toLowerCase().includes(query)
    );
    return nameMatch || typeMatch || locationMatch || serviceMatch;
  });
}

const PLATFORM_MANUAL_PARENT_OPTION = VENDOR_LIST_PLATFORM_MANUAL_OPTIONS.find(
  (option) => option.isParent
);
const PLATFORM_MANUAL_CHILD_VALUES = PLATFORM_MANUAL_PARENT_OPTION?.children || [];
const PLATFORM_MANUAL_PARENT_VALUES = [
  PLATFORM_MANUAL_PARENT_OPTION?.value,
  "requested-in-platform",
].filter(Boolean);

function vendorMatchesPlatformManual(vendor, selected = []) {
  if (!selected.length) return true;

  const selectedSet = new Set(selected);
  const hasMy = (vendor.myRequests || []).length > 0;
  const hasLibrary = (vendor.libraryRequests || []).length > 0;
  const isManual = isAddedManuallyVendor(vendor);
  const isPlatform = isRequestedInPlatformVendor(vendor);

  const selectedChildren = PLATFORM_MANUAL_CHILD_VALUES.filter((value) =>
    selectedSet.has(value)
  );
  const parentSelected = PLATFORM_MANUAL_PARENT_VALUES.some((value) =>
    selectedSet.has(value)
  );
  const allChildrenSelected =
    PLATFORM_MANUAL_CHILD_VALUES.length > 0 &&
    selectedChildren.length === PLATFORM_MANUAL_CHILD_VALUES.length;
  const wantsAllRequested =
    allChildrenSelected || (parentSelected && selectedChildren.length === 0);
  const wantsNewRequest = selectedSet.has("new-request");
  const wantsLibraryRequest = selectedSet.has("library-request");
  const wantsManual = selectedSet.has("manual") || selectedSet.has("added-manually");

  return (
    (wantsAllRequested && isPlatform) ||
    (!wantsAllRequested && wantsNewRequest && hasMy) ||
    (!wantsAllRequested && wantsLibraryRequest && hasLibrary && isPlatform) ||
    (wantsManual && isManual)
  );
}

function vendorMatchesTags(vendor, selected = []) {
  if (!selected.length) return true;
  const tags = new Set(getVendorTagList(vendor).map((tag) => String(tag).toLowerCase()));
  return selected.some((tag) => tags.has(String(tag).toLowerCase()));
}

function normalizeRequestStatusFilterValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
}

function vendorMatchesRequestStatus(vendor, selected = []) {
  if (!selected.length) return true;

  const requests = collectVendorListViewRequests(vendor);
  if (!requests.length) return false;

  const normalized = selected.map(normalizeRequestStatusFilterValue);
  const wantsComplete = normalized.some((value) =>
    REQUEST_STATUS_COMPLETE_VALUES.has(value)
  );
  const wantsInProgress = normalized.some((value) =>
    REQUEST_STATUS_IN_PROGRESS_VALUES.has(value)
  );

  return requests.some((request) => {
    const key = resolveRequestStatusKey(request);
    if (wantsComplete && key === "complete") return true;
    if (wantsInProgress && (key === "in-progress" || key === "pending")) return true;
    return false;
  });
}

/** Within-list filter pass — mirrors Innovation Lab VendorListView matcher. */
export function applyVendorListViewFilters(vendors = [], selectedIds = {}) {
  const selected = selectedIds || EMPTY_VENDOR_LIST_VIEW_SELECTED_IDS;

  return vendors.filter((vendor) => {
    if (selected.type?.length && !selected.type.includes(vendor.type)) return false;

    if (selected.gxp?.length) {
      const vendorGxPs = collectVendorListViewGxPs(vendor);
      if (!selected.gxp.some((gxp) => vendorGxPs.includes(gxp))) return false;
    }

    if (selected.services?.length) {
      const services = collectVendorListViewServices(vendor);
      if (!selected.services.some((service) => services.includes(service))) return false;
    }

    if (selected.addedBy?.length && !selected.addedBy.includes(vendor.addedBy)) return false;
    if (!vendorMatchesTags(vendor, selected.tags)) return false;
    if (!vendorMatchesPlatformManual(vendor, selected.platformManual)) return false;

    if (DOCUMENT_RISK_SCORE_FILTER_ENABLED && selected.riskScores?.length) {
      const ratings = collectVendorRiskRatings(vendor);
      if (!selected.riskScores.some((rating) => ratings.includes(rating))) return false;
    }

    if (!vendorMatchesRequestStatus(vendor, selected.requestStatus)) return false;

    return true;
  });
}

/** Filter sections for within-list view — order and ids match Innovation Lab getVendorListViewFilters. */
export function buildVendorListViewFilterSections({ listVendors = [] } = {}) {
  const sections = [];
  const vendorTypesSet = new Set();
  const gxPsSet = new Set();
  const servicesSet = new Set();
  const addedBySet = new Set();
  const tagsSet = new Set();
  let hasRequests = false;

  listVendors.forEach((vendor) => {
    if (collectVendorListViewRequests(vendor).length > 0) {
      hasRequests = true;
    }
    if (vendor.type) vendorTypesSet.add(vendor.type);
    collectVendorListViewGxPs(vendor).forEach((gxp) => gxPsSet.add(gxp));
    collectVendorListViewServices(vendor).forEach((service) => servicesSet.add(service));
    if (vendor.addedBy) addedBySet.add(vendor.addedBy);
    getVendorTagList(vendor).forEach((tag) => tagsSet.add(tag));
  });

  if (vendorTypesSet.size > 0) {
    sections.push({
      id: "type",
      name: "Vendor Type",
      searchable: false,
      options: Array.from(vendorTypesSet)
        .sort((a, b) => a.localeCompare(b))
        .map((type) => ({ value: type, label: type })),
      total: vendorTypesSet.size,
    });
  }

  sections.push({
    id: "platformManual",
    name: "Platform / Manual",
    searchable: false,
    hierarchical: true,
    options: VENDOR_LIST_PLATFORM_MANUAL_OPTIONS,
    total: VENDOR_LIST_PLATFORM_MANUAL_OPTIONS.length,
  });

  if (gxPsSet.size > 0) {
    sections.push({
      id: "gxp",
      name: "GxPs",
      searchable: false,
      options: Array.from(gxPsSet)
        .sort((a, b) => a.localeCompare(b))
        .map((gxp) => ({
          value: gxp,
          label: GXP_DESCRIPTORS[gxp] || gxp,
        })),
      total: gxPsSet.size,
    });
  }

  if (servicesSet.size > 0) {
    sections.push({
      id: "services",
      name: "Services",
      searchable: true,
      options: Array.from(servicesSet)
        .sort((a, b) => a.localeCompare(b))
        .map((service) => ({ value: service, label: service })),
      total: servicesSet.size,
    });
  }

  if (addedBySet.size > 0) {
    sections.push({
      id: "addedBy",
      name: "Added By",
      searchable: false,
      options: Array.from(addedBySet)
        .sort((a, b) => a.localeCompare(b))
        .map((user) => ({ value: user, label: user })),
      total: addedBySet.size,
    });
  }

  if (tagsSet.size > 0) {
    sections.push({
      id: "tags",
      name: "Custom Tags",
      searchable: true,
      options: Array.from(tagsSet)
        .sort((a, b) => a.localeCompare(b))
        .map((tag) => ({ value: tag, label: tag })),
      total: tagsSet.size,
    });
  }

  if (hasRequests) {
    sections.push({
      id: "requestStatus",
      name: "Request Status",
      searchable: false,
      options: REQUEST_STATUS_FILTER_OPTIONS,
      total: REQUEST_STATUS_FILTER_OPTIONS.length,
    });
  }

  if (DOCUMENT_RISK_SCORE_FILTER_ENABLED) {
    sections.push({
      id: "riskScores",
      name: "Document Risk Score",
      searchable: false,
      options: DOCUMENT_RISK_SCORE_FILTER_OPTIONS,
      total: DOCUMENT_RISK_SCORE_FILTER_OPTIONS.length,
    });
  }

  return sections;
}
